import React, { useCallback, useMemo } from 'react'
import Typography from '../../components/typography'
import AuthPageLayout from '../../layout/auth_page_layout'
import { useOnInputChange } from '../../hooks/useOnInputChange'
import TextField from '../../components/textfield'
import Button from '../../components/button'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useSetCurrentUser from '../../hooks/useSetCurrentUser'
import { AuthService } from '../../apilibrary'
import { NewPasswordRequiredError, UserNotConfirmedError } from '../../apilibrary/auth'
import Link from '../../components/router/link'
import { Divider } from '@mui/material'
import useCurrentUserId from '../../hooks/useCurrentUserId'
import { trackUserLoginFailure, trackUserLoginSuccess } from '../../utils/analytics'
import NewPasswordRequiredForm from './new_password_required'
import Box from '../../components/box'

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [email, onChangeEmail] = useOnInputChange(searchParams.get('email') ?? '')
  const [password, onChangePassword, setPassword] = useOnInputChange('')
  const [disableButton, setDisableButton] = React.useState(false)
  const [newPasswordRequired, setNewPasswordRequired] = React.useState<boolean>(false)
  const [cognitoUser, setCognitoUser] = React.useState<any>(null)
  const [error, setError] = React.useState<string>()
  const setUser = useSetCurrentUser()
  const navigate = useNavigate()
  const userId = useCurrentUserId();

  const handleClickLogin = useCallback(async (): Promise<void> => {
    setError(undefined)
    if (email.length === 0 || password.length === 0) {
      setError('Email and password are required')
      return
    }
    try {
      setDisableButton(true)
      const user = await AuthService.login(email, password)
      setUser({
        id: user.id
      })
      const redirect = searchParams.get('redirect') ?? '/'
      await trackUserLoginSuccess(userId);
      navigate(redirect)
    } catch (e) {
      console.log(e)
      if (e instanceof UserNotConfirmedError) {
        navigate('/auth/confirmation?email=' + email)
      } else if (e instanceof NewPasswordRequiredError) {
        setNewPasswordRequired(true)
        setCognitoUser(e.cognitoUser)
      } else if (e instanceof Error) {
        setError(e.message)
      } else {
        setError('Something went wrong')
      }
      await trackUserLoginFailure(userId);
      setDisableButton(false)
    }
  }, [email, navigate, password, searchParams, setUser, userId])

  const onResetPasswordComplete = useCallback(async (newPassword: string): Promise<void> => {
    setNewPasswordRequired(false)
    setPassword(newPassword)
  }, [setPassword])

  const form = useMemo(() => {
    if (newPasswordRequired) {
      return (
        <NewPasswordRequiredForm email={email} cognitoUser={cognitoUser} onPasswordResetComplete={onResetPasswordComplete} />
      )
    }
    return (
      <Box>
        <TextField
        fullWidth
        gutterBottom
        value={email}
        onChange={onChangeEmail}
        label='Email'
      />
        <TextField
        fullWidth
        gutterBottom
        value={password}
        onChange={onChangePassword}
        label='Password'
        type='password'
      />
        <Button
        fullWidth
        sx={{ marginTop: 2 }}
        disabled={disableButton}
        onClick={handleClickLogin}
        variant='contained'
        color='primary'>
          Login
        </Button>
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Link to='/auth/forget-password' removeTextDecoration>
          <Button variant='text' color='secondary' fullWidth>
            Forgot password?
          </Button>
        </Link>
        <Link to='/auth/signup' removeTextDecoration>
          <Button fullWidth variant='text' color='primary'>
            Don&apos;t have an account? Sign up
          </Button>
        </Link>
      </Box>
    )
  }, [cognitoUser, disableButton, email, handleClickLogin, newPasswordRequired, onChangeEmail, onChangePassword, onResetPasswordComplete, password])

  return (
    <AuthPageLayout title='Login'>
      {(Boolean(error)) && <Typography color={'error'}>{error}</Typography>}
      {form}
    </AuthPageLayout>
  )
}

export default LoginPage
